/**
 * PD-17646 — booking pages (docs/claude/campaigns/09-booking-pages.md). The fixed lists a
 * booking type and a booking use, and the rules for the names in a booking link
 * (`<base>/<person>/<type>`), shared by the settings screens and the server.
 */

/** Where a meeting happens. Teams adds a join link to the invite. */
export const BOOKING_WHERE_KINDS = ['teams', 'phone', 'in_person'] as const;
export type BookingWhereKind = (typeof BOOKING_WHERE_KINDS)[number];

export const BOOKING_WHERE_LABELS: Record<BookingWhereKind, string> = {
	teams: 'Microsoft Teams',
	phone: 'Phone',
	in_person: 'In person',
};

/** On: offered on the person's page. Hidden: only by link. Off: no longer bookable. */
export const BOOKING_TYPE_STATUSES = ['on', 'hidden', 'off'] as const;
export type BookingTypeStatus = (typeof BOOKING_TYPE_STATUSES)[number];

/** Anyone with the link, or only the people a personal link was sent to. */
export const BOOKING_WHO_CAN_BOOK = ['anyone', 'link_recipients'] as const;
export type BookingWhoCanBook = (typeof BOOKING_WHO_CAN_BOOK)[number];

export const BOOKING_STATUSES = ['booked', 'rescheduled', 'cancelled', 'met', 'no_show'] as const;
export type BookingStatus = (typeof BOOKING_STATUSES)[number];

/** A booking in these statuses holds its time. */
export const BOOKING_ACTIVE_STATUSES: ReadonlyArray<BookingStatus> = ['booked', 'rescheduled'];

/** A meeting that happened, or was missed: it stays on the grid at its time. */
export const BOOKING_HELD_STATUSES: ReadonlyArray<BookingStatus> = ['met', 'no_show'];

export const BOOKING_QUESTION_KINDS = ['short', 'long'] as const;
export type BookingQuestionKind = (typeof BOOKING_QUESTION_KINDS)[number];

/** A question a booking type asks, on top of name, company, email and mobile. */
export interface BookingQuestionModel {
	id: string;
	label: string;
	kind: BookingQuestionKind;
	required: boolean;
}

/** PD-17649 — where a booth booking block came from. */
export const BOOTH_BLOCK_SOURCES = ['show_hours', 'changed', 'added'] as const;
export type BoothBlockSource = (typeof BOOTH_BLOCK_SOURCES)[number];

export const BOOTH_BLOCK_SOURCE_LABELS: Record<BoothBlockSource, string> = {
	show_hours: 'Show Hours',
	changed: 'Changed',
	added: 'Added',
};

/** What a host marks after a booth meeting. */
export const BOOTH_MEETING_OUTCOMES = ['met', 'no_show'] as const;
export type BoothMeetingOutcome = (typeof BOOTH_MEETING_OUTCOMES)[number];

/** The `booking_name` scope of a show's booking link, `<base>/show/<name>`. */
export const BOOKING_NAME_SHOW_SCOPE = 'show';

/** Lengths the guest form and the server both enforce. */
export const BOOKING_LIMITS = {
	nameLength: 120,
	companyLength: 160,
	emailLength: 320,
	phoneLength: 32,
	answerLength: 1000,
	questions: 10,
	/** How many days of open times one read can ask for. */
	timesWindowDays: 62,
} as const;

export const BOOKING_NAME_MIN_LENGTH = 2;
export const BOOKING_NAME_MAX_LENGTH = 40;

/**
 * Names the address space needs for itself: the fixed paths under `/book/`, the lasting
 * print links, and words a person could use to pass as the company.
 */
export const BOOKING_RESERVED_NAMES: ReadonlySet<string> = new Set([
	'about',
	'admin',
	'api',
	'app',
	'book',
	'booking',
	'booth',
	'calendar',
	'cancel',
	'demo',
	'help',
	'home',
	'login',
	'manage',
	'meet',
	'meeting',
	'portal',
	'register',
	'reschedule',
	'sales',
	'settings',
	'show',
	'sign-in',
	'support',
	'team',
	'workd',
]);

export type BookingNameProblem = 'too_short' | 'too_long' | 'bad_characters' | 'reserved';

/** Lowercase letters, numbers and hyphens, with a letter or number at both ends. */
const BOOKING_NAME_PATTERN = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;

/**
 * Why this name can't be a link name, or null when it can. `allowReserved` is for the
 * type part: `/book/chris/demo` is the example in the spec, and the reserved list only
 * protects the first part of the path.
 */
export function bookingNameProblem(
	name: string,
	options: { allowReserved?: boolean } = {},
): BookingNameProblem | null {
	if (name.length < BOOKING_NAME_MIN_LENGTH) {
		return 'too_short';
	}

	if (name.length > BOOKING_NAME_MAX_LENGTH) {
		return 'too_long';
	}

	if (!BOOKING_NAME_PATTERN.test(name)) {
		return 'bad_characters';
	}

	if (options.allowReserved !== true && BOOKING_RESERVED_NAMES.has(name)) {
		return 'reserved';
	}

	return null;
}

/** A link name suggested from free text: a person's name or a booking type's title. */
export function suggestBookingName(text: string | null | undefined): string {
	const name = (text ?? '')
		.normalize('NFKD')
		.replace(/[̀-ͯ]/g, '')
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '')
		.slice(0, BOOKING_NAME_MAX_LENGTH)
		.replace(/-+$/, '');

	return name;
}
